import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import KeyCodeListener from 'app/components/v2/components/EventListeners/KeyCodeListener';
import { EvtKeys } from 'app/types';

import Dropdown from './Dropdown';
import styles from '../../constants/styles';
import { ResizeObserverDimensions, useDidMount, useResizeObserver } from '../../hooks';
import Box from '../Box';
import Debouncer from '../Debouncer';
import { IconSize } from '../Icon';
import IconButton from '../IconButton';
import Input, { InputProps } from '../Input';
import Wrapper from '../Input/Wrapper';
import LoadingSpinner from '../LoadingSpinner';

// Local Typings
export interface TypeAheadOption {
  id: number | string;
  label: string;
  sublabel?: string;
}
interface GetSecondaryElementArgs {
  disabled: boolean;
  iconSize: IconSize;
}
export interface BaseProps extends InputProps {
  allowCustomInput?: boolean;
  debounce?: boolean;
  debounceDuration?: number;
  disablePaste?: boolean | undefined;
  hideDropdownIfNoResults?: boolean;
  inputValue: string;
  isClearHidden?: boolean;
  isFetching?: boolean;
  isSelected: boolean;
  onChange: (evt: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  onDeselect?: () => void;
  onTypeAheadSelect: (option: TypeAheadOption) => void;
  options: TypeAheadOption[];
  selectedId: null | number | string;
  showDropdownAfterInteraction?: boolean;
  showDropdownOnEmptyInput?: boolean;
  showSelectedId?: boolean;
  value: string;
}

interface WithClientSideFiltering {
  onUpdateOptions?: never;
  withClientSideFiltering?: true;
}

interface WithServerSideFiltering {
  onUpdateOptions: (inputValue: string) => void;
  withClientSideFiltering?: never;
}

export type TypeAheadProps = BaseProps & (WithClientSideFiltering | WithServerSideFiltering);

// Local Variables
function renderSpinner(iconSize: IconSize) {
  return (
    <Box padding={1}>
      <LoadingSpinner iconSize={iconSize} />
    </Box>
  );
}

// Component Definition
const TypeAhead: FC<TypeAheadProps> = ({
  allowCustomInput,
  debounce,
  debounceDuration,
  hideDropdownIfNoResults,
  inputSize,
  inputValue,
  isClearHidden,
  isFetching = false,
  isSelected,
  onClear,
  onDeselect,
  onTypeAheadSelect,
  onUpdateOptions,
  options,
  selectedId,
  showDropdownAfterInteraction,
  showDropdownOnEmptyInput,
  showSelectedId,
  ...props
}) => {
  const didMount = useDidMount();
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isDropdownFocused, setIsDropdownFocused] = useState(false);
  const [isComponentInteractedWith, setIsComponentInteractedWith] = useState(false);
  const [wrapperDimensions, setWrapperDimensions] = useState<null | ResizeObserverDimensions>(null);

  useResizeObserver(wrapperRef,
    setWrapperDimensions);

  function handleDebounce(value: string) {
    handleIsInteractedWith();
    onUpdateOptions?.(value);
  }

  function handleBlur() {
    setTimeout(() => {
      setIsInputFocused(false);
    },
    0);
  }

  function handleDropdownBlur() {
    setTimeout(() => {
      setIsDropdownFocused(false);
    },
    0);
  }

  function handleDropdownFocus() {
    setIsDropdownFocused(true);
  }

  function handleFocus() {
    setIsInputFocused(true);
  }

  function handleClear() {
    onClear();
    setIsInputFocused(false);
    setIsDropdownFocused(false);
  }

  function getSecondaryElement({ disabled, iconSize }: GetSecondaryElementArgs) {
    if (isFetching) {
      return renderSpinner(iconSize);
    }

    if (!!(selectedId !== null || props.prefixElement) && !disabled && !isClearHidden) {
      return (
        <IconButton
          collapse
          iconName="close"
          iconSize="xxs"
          minimize
          onClick={handleClear}
          withPaddingY
        />
      );
    }

    return null;
  }

  useEffect(() => {
    if (didMount && !debounce) {
      onUpdateOptions?.(inputValue);
    }
  },
  [debounce, didMount, inputValue, onUpdateOptions]);

  useEffect(() => {
    if (!isSelected) {
      onDeselect?.();
    }
  },
  [isSelected, onDeselect]);

  useEffect(() => {
    // if either item just blurred while and both are now blurred
    if (!isDropdownFocused && !isInputFocused) {
      const foundOptionById = options.find(option => option.id === selectedId);
      const foundOptionByLabel = options.find(option => option.label.toLowerCase() === inputValue.toLowerCase());

      const foundOption = foundOptionById ?? foundOptionByLabel;

      if (foundOption) {
        onTypeAheadSelect(foundOption);
      } else if (allowCustomInput && !!inputValue.trim()) {
        onTypeAheadSelect({
          id: inputValue,
          label: inputValue,
        });
      }
    }
  },
  [
    allowCustomInput,
    inputValue,
    isDropdownFocused,
    isInputFocused,
    onTypeAheadSelect,
    options,
    selectedId,
  ]);

  const handleIsInteractedWith = useCallback(() => {
    setIsComponentInteractedWith(true);
  }, [setIsComponentInteractedWith]);

  useEffect(() => {
    if (!isComponentInteractedWith && isDropdownFocused) {
      handleIsInteractedWith();
    }
  },
  [isComponentInteractedWith, isDropdownFocused, handleIsInteractedWith]);

  const onChange = (evt: ChangeEvent<HTMLInputElement>) => {
    handleIsInteractedWith();
    props.onChange(evt);
  };

  const inputProps: InputProps = {
    ...props,
    error: !!props.error && !isInputFocused && !isDropdownFocused && isComponentInteractedWith,
    inputRef,
    onBlurCapture: handleBlur,
    onClick: handleIsInteractedWith,
    onFocus: handleFocus,
    placeholder: props.placeholder,
    secondaryElement: getSecondaryElement({
      disabled: !!props.disabled,
      iconSize: inputSize === 'small' ? 'xxs' : 'xs',
    }),
    secondaryElementAlignSelf: 'center',
    value: (showSelectedId && selectedId !== null) ? selectedId.toString() : inputValue,
  };

  const visibleOptions = useMemo(() => {
    if (onUpdateOptions || !inputValue) {
      return options;
    }

    if (props.withClientSideFiltering) {
      return options.filter(opt => opt.label.toLowerCase().includes(inputValue.toLowerCase()));
    }

    // this shouldn't be possible... not sure how to avoid this branch though.
    return options;
  },
  [onUpdateOptions, inputValue, options, props.withClientSideFiltering]);

  return (
    <Wrapper
      inputSizeX={props.inputSizeX}
      ref={wrapperRef}
    >
      <KeyCodeListener
        keyCodeActions={[
          {
            action: handleIsInteractedWith,
            key: EvtKeys.Down,
          },
          {
            action: handleIsInteractedWith,
            key: EvtKeys.Enter,
          },
          {
            action: handleIsInteractedWith,
            key: EvtKeys.Up,
          },
        ]}
      >
        {debounce
          ? (
            <Debouncer
              debounceDuration={debounceDuration}
              onChange={onChange}
              onDebounce={handleDebounce}
            >
              {({
                isDebouncing,
                onChange: handleChange,
              }) => (
                <Input
                  {...inputProps}
                  inputSize={inputSize}
                  onChange={handleChange}
                  secondaryElement={isDebouncing
                    ? (
                      renderSpinner(inputSize === 'small' ? 'xxs' : 'xs')
                    )
                    : inputProps.secondaryElement}
                  secondaryElementAlignSelf="auto"
                />
              )}
            </Debouncer>
          )
          : (
            <Input
              {...inputProps}
              onChange={onChange}
            />
          )}
      </KeyCodeListener>

      {!isSelected && (isInputFocused || isDropdownFocused) && (
        <Dropdown
          currentInputValue={inputValue}
          dropdownPositionTop={wrapperDimensions?.height
            ? wrapperDimensions.height + (styles.borderWidths.default ?? 0) * 2
            : undefined}
          hideDropdownIfNoResults={!!hideDropdownIfNoResults}
          onBlur={handleDropdownBlur}
          onFocus={handleDropdownFocus}
          onSelect={onTypeAheadSelect}
          options={visibleOptions}
          showDropdownOnEmptyInput={!!showDropdownOnEmptyInput
            && (!showDropdownAfterInteraction || isComponentInteractedWith)}
        />
      )}
    </Wrapper>
  );
};

export default TypeAhead;
